import { useCallback, useMemo } from "react";
import { useQueryClient } from "react-query";
import type { NearMeFilterConfiguration } from "@/react/components/FilterKit/types";
import type {
  LocationData,
  MapBoxFilterParams,
} from "@/react/components/shared/Filter/FilterTypes/MapboxInputFilter";
import { FilterButtonV2 } from "@/react/components/shared/FilterV3/FilterButtonV2";
import { Icon } from "@/react/components/shared/Icon";
import { useUpdateLocationModal } from "../../shared/MemberFilters/SharedFilters/NearMe/UpdateLocationModal";
import { useFilterProvider } from "../FilterProvider";

interface NearMeFilterInputProps {
  configuration: NearMeFilterConfiguration;
}

export const NearMeFilterInput = ({
  configuration,
}: NearMeFilterInputProps) => {
  const { setFilter, clearFilterByKey, filterParamForKey } =
    useFilterProvider();
  const queryClient = useQueryClient();

  const param = useMemo((): MapBoxFilterParams => {
    const existingFilter = filterParamForKey(configuration.key) as
      | MapBoxFilterParams
      | undefined;

    return {
      id: configuration.key,
      key: configuration.key,
      value: existingFilter?.value,
    };
  }, [filterParamForKey, configuration.key]);

  const hasValue = Boolean(param.value);

  const updateLocationModal = useUpdateLocationModal();

  const handleClick = useCallback(async () => {
    if (param?.value) {
      clearFilterByKey(configuration.key);
      return;
    }

    if (!configuration.payload.require_user_to_provide_more_precise_location) {
      setFilter(configuration.key, {
        ...param,
        search_key: configuration.search_key,
        value: {
          id: configuration.payload.near_me_location_filter_values?.id,
          name: configuration.payload.near_me_location_filter_values?.name,
          source: "mapbox_v6",
        },
      });
      return;
    }

    await updateLocationModal.show({
      payload: configuration.payload,
      onSuccess: (data: { location: LocationData }) => {
        setFilter(configuration.key, {
          ...param,
          search_key: configuration.search_key,
          key: configuration.key,
          value: data.location,
        });
        void updateLocationModal.hide();
        void queryClient.invalidateQueries(["crmFilters"]);
      },
    });
  }, [
    configuration,
    updateLocationModal,
    param,
    setFilter,
    queryClient,
    clearFilterByKey,
  ]);

  const handleClear = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      clearFilterByKey?.(configuration.key);
    },
    [clearFilterByKey, configuration.key],
  );

  if (!configuration.enabled) {
    return null;
  }

  return (
    <FilterButtonV2
      hasValue={hasValue}
      onClear={handleClear}
      onClick={handleClick}
      className="py-1.5 pl-[10px] pr-3"
    >
      <Icon
        type="16-location-filled"
        size={16}
        className="text-dark"
        useWithFillCurrentColor
      />
      <span className="text-dark text-sm font-medium leading-5">
        {configuration.label}
      </span>
    </FilterButtonV2>
  );
};
